import { debounce, ensureFocusVisiblePolyfill, pauseAllMedia, removeTrapFocus, trapFocus } from "../lib/ui-helpers.ts";
import { trackEvent } from "../utils/analytics.ts";
import { attachHTMLUpdateUtilityToWindow } from "../lib/html-update-utility.ts";
import { attachLegacyMembershipGlobals } from "../lib/membership-notifications.ts";
import { defineSliderComponentCustomElement } from "../modules/slider-component.ts";
import { defineMediaGalleryCustomElement } from "../modules/media-gallery.ts";
import { defineModalDialogCustomElement } from "../modules/modal-dialog.ts";
import { defineProductRecommendationsCustomElement } from "../modules/product-recommendations.ts";
import { defineDeferredMedia } from "../modules/deferred-media.ts";

declare global {
  interface Window {
    ON_CHANGE_DEBOUNCE_TIMER?: number;
    PUB_SUB_EVENTS?: Record<string, string>;
    subscribe?: (eventName: string, callback: (data: any) => void) => () => void;
    publish?: (eventName: string, data?: any) => void;

    debounce?: typeof debounce;
    trapFocus?: typeof trapFocus;
    removeTrapFocus?: typeof removeTrapFocus;
    pauseAllMedia?: typeof pauseAllMedia;

    waitForSwiper?: () => Promise<void>;

    waitForKlaviyo?: () => Promise<void>;
    initKlaviyo?: () => void;

    waitForLoyalty?: () => Promise<void>;

    klaviyo?: any;

    currentMembership?: any;

    currentLang?: string;

    loyaltyText?: any;

    initateClipboardButton?: (button: HTMLElement, notificationText?: string) => void;
  }
}

declare const Klaviyo: any;
declare const Choices: any;
declare const Swiper: unknown;
declare const routes: {
  cart_url: string;
  cart_update_url: string;
  cart_change_url: string;
};
declare const _ALConfig: unknown;
declare function gtag(...args: any[]): void;

type SupportedLang = "fr" | "en";
type LocalizedText<T> = Record<SupportedLang, T>;

const ON_CHANGE_DEBOUNCE_TIMER = 300;

const PUB_SUB_EVENTS = {
  cartUpdate: "cart-update",
  quantityUpdate: "quantity-update",
  optionValueSelectionChange: "option-value-selection-change",
  variantChange: "variant-change",
  cartError: "cart-error",
};

type PubSubCallback = (data: any) => void;

let subscribers: Record<string, PubSubCallback[]> = {};

function subscribe(eventName: string, callback: PubSubCallback) {
  if (subscribers[eventName] === undefined) {
    subscribers[eventName] = [];
  }

  subscribers[eventName] = [...subscribers[eventName], callback];

  return function unsubscribe() {
    subscribers[eventName] = subscribers[eventName].filter((cb) => {
      return cb !== callback;
    });
  };
}

function publish(eventName: string, data?: any) {
  if (subscribers[eventName]) {
    subscribers[eventName].forEach((callback) => {
      callback(data);
    });
  }
}

window.ON_CHANGE_DEBOUNCE_TIMER = ON_CHANGE_DEBOUNCE_TIMER;
window.PUB_SUB_EVENTS = PUB_SUB_EVENTS;
window.subscribe = subscribe;
window.publish = publish;

window.debounce = debounce;
window.trapFocus = trapFocus;
window.removeTrapFocus = removeTrapFocus;
window.pauseAllMedia = pauseAllMedia;
// Exposed for cart-upsell-engine.js, a classic <script> that cannot import modules.
window.findSameNameProducts = async (opts) => (await import("../modules/bundle-storefront.ts")).findSameNameProducts(opts);

attachHTMLUpdateUtilityToWindow();
defineSliderComponentCustomElement();
defineMediaGalleryCustomElement();
defineModalDialogCustomElement();
defineProductRecommendationsCustomElement();
defineDeferredMedia();
attachLegacyMembershipGlobals();

if (typeof (window as any).currentMembership === "undefined") {
  (window as any).currentMembership = null;
}

if (typeof (window as any).currentLang === "undefined") {
  const docLang = document.documentElement.lang?.toLowerCase();
  (window as any).currentLang = docLang?.startsWith("fr") ? "fr" : "en";
}

// Expose legacy helpers that used to be function-scoped globals
window.waitForSwiper = waitForSwiper;
// Legacy helper for membership-account.js
(window as any).waitForMembership = waitForMembership;
(globalThis as any).waitForMembership = waitForMembership;

if (document.querySelector('script[src*="https://a.klaviyo.com/media/js/onsite/onsite.j"]')) {
  import("../modules/klaviyo").then(({ initKlaviyoModule }) => initKlaviyoModule());
}

// Every page: adds also happen from collection quick add, bundles and the cart drawer.
// Events queue on window.klaviyo until onsite.js loads (deferred to window load off PDPs).
import("../modules/klaviyo-added-to-cart").then(({ initKlaviyoAddedToCart }) => initKlaviyoAddedToCart());

if (document.querySelector('[data-user-connect][data-user-connect="true"]') || document.querySelector(".membership-select-mobile-menu") || document.querySelector("#membership-menu") || document.querySelector("article.membership-welcome")) {
  (window as any).__membershipModulePromise =
    (window as any).__membershipModulePromise ||
    import("../modules/membership")
      .then(({ initMembershipModule }) => initMembershipModule())
      .catch((err) => {
        (window as any).__membershipModuleError = err;
        throw err;
      });
}

if (document.querySelector(".announcement-bar") || document.querySelector("[data-datevisible],[data-datehidden]")) {
  import("../modules/announcement.ts").then(({ initAnnouncementAndDetails }) => initAnnouncementAndDetails());
}

if (document.querySelector('[id^="Details-"] summary')) {
  import("../modules/details-summary").then(({ initDetailsSummaryA11y }) =>
    initDetailsSummaryA11y({
      onKeyUpEscape,
    }),
  );
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
ensureFocusVisiblePolyfill();

function onKeyUpEscape(event: KeyboardEvent) {
  if (event.code.toUpperCase() !== "ESCAPE") return;

  const openDetailsElement = (event.target as HTMLElement | null)?.closest("details[open]");
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector<HTMLElement>("summary");
  openDetailsElement.removeAttribute("open");
  if (summaryElement) {
    summaryElement.setAttribute("aria-expanded", "false");
    summaryElement.focus();
  }
}

if (document.querySelector("quantity-input")) {
  import("../modules/quantity-input").then(({ defineQuantityInputCustomElement }) => defineQuantityInputCustomElement());
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: `application/${type}` },
  };
}

window.fetchConfig = fetchConfig;
(globalThis as any).fetchConfig = fetchConfig;

// Custom cookie management functions

window.Shopify = window.Shopify || {};

window.Shopify.checkCookie = function (name: string) {
  var nameEQ = name + "=";
  var ca = document.cookie.split(";");
  for (var i = 0; i < ca.length; i++) {
    var c = ca[i];
    while (c.charAt(0) == " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

window.Shopify.setCookie = function (name: string, value: string, days?: number) {
  var expires = "";
  if (days) {
    var date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = "; expires=" + date.toUTCString();
  }
  document.cookie = name + "=" + (value || "") + expires + "; path=/";
};

window.Shopify.eraseCookie = function (name: string) {
  document.cookie = name + "=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";
};

// Formatting functions for phone models

window.Shopify.escapeRegExp = function (string: string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // $& means the whole matched string
};

window.Shopify.formatPhoneModel = function (phoneLabel: string, match: string, replacement: string) {
  const escaped = match.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const phoneLabelMods = phoneLabel.toLowerCase().replace(/[^\w\s]/gi, "");
  return phoneLabelMods.replace(new RegExp(escaped, "g"), () => replacement);
};

const notificationButton = document.querySelector("[data-open-notification-menu]");
if (notificationButton) {
  const notificationsDialog = document.querySelector<HTMLDialogElement>("#notifications-menu");
  if (notificationsDialog) {
    if (!window.Shopify.checkCookie("has-notification")) {
      notificationButton.classList.add("--dot");
    }
    notificationButton.addEventListener("click", () => {
      notificationsDialog.showModal();
      document.body.classList.add("--no-scroll");
    });

    const notificationNavCloseButton = document.querySelector("[data-close-notifications-menu]");
    if (notificationNavCloseButton) {
      notificationNavCloseButton.addEventListener("click", (e) => {
        e.preventDefault();
        notificationsDialog.close();
        document.body.classList.remove("--no-scroll");
        window.Shopify?.setCookie?.("has-notification", "true", 1);
        notificationButton.classList.remove("--dot");
      });
    }

    notificationsDialog.addEventListener("click", function (event) {
      if (event.target === notificationsDialog) {
        notificationsDialog.close();
        document.body.classList.remove("--no-scroll");
      }
    });

    notificationsDialog.querySelectorAll<HTMLButtonElement>(".dialog-header ul button[data-target]").forEach((categoryButton) => {
      categoryButton.addEventListener("click", (e) => {
        e.preventDefault();
        notificationsDialog.querySelectorAll<HTMLButtonElement>(".dialog-header ul button.--active").forEach((activeBtn) => {
          activeBtn.classList.remove("--active");
        });
        categoryButton.classList.add("--active");
        let currentlyVisibleCounter = 0;
        notificationsDialog.querySelectorAll<HTMLElement>(".dialog-content .notification").forEach((notification) => {
          if (categoryButton.dataset.target !== "all") {
            if (notification.dataset.category === categoryButton.dataset.target) {
              notification.style.display = "flex";
              currentlyVisibleCounter++;
            } else {
              notification.style.display = "none";
            }
          } else {
            notification.style.display = "flex";
            currentlyVisibleCounter++;
          }
        });
        if (currentlyVisibleCounter == 0) {
          const empty = notificationsDialog.querySelector<HTMLElement>(".empty-notification");
          if (empty) empty.style.display = "block";
        } else {
          const empty = notificationsDialog.querySelector<HTMLElement>(".empty-notification");
          if (empty) empty.style.display = "none";
        }
      });
    });
  } else {
    notificationButton.remove();
  }
}

const membershipNavOpenButton = document.querySelector("[data-open-membership-menu]");
const membershipDialog = document.querySelector<HTMLDialogElement>("#membership-menu");
if (membershipNavOpenButton && membershipDialog) {
  membershipNavOpenButton.addEventListener("click", (e) => {
    e.preventDefault();
    membershipDialog.showModal();
    document.body.classList.add("--no-scroll");
  });
}
const membershipNavCloseButton = document.querySelector("[data-close-membership-menu]");
if (membershipNavCloseButton && membershipDialog) {
  membershipNavCloseButton.addEventListener("click", (e) => {
    e.preventDefault();
    membershipDialog.close();
    document.body.classList.remove("--no-scroll");
  });
}

if (membershipDialog) {
  membershipDialog.addEventListener("click", function (event) {
    if (event.target === membershipDialog) {
      membershipDialog.close();
      document.body.classList.remove("--no-scroll");
    }
  });
}

function waitForMembership(timeoutMs = 15000): Promise<void> {
  const debug = ((window as any).__membershipWaitDebug = (window as any).__membershipWaitDebug || {
    calls: [],
  });
  const call = { startedAt: Date.now(), timeoutMs } as any;
  debug.calls.push(call);

  // Ensure the membership module is scheduled to load.
  // Some pages run membership-account.js but may not match the initial lazy-load selectors.
  if (!(window as any).__membershipModulePromise) {
    call.scheduledImportAt = Date.now();
    (window as any).__membershipModulePromise = import("../modules/membership")
      .then(({ initMembershipModule }) => initMembershipModule())
      .catch((err) => {
        (window as any).__membershipModuleError = err;
        throw err;
      });
  }

  const maybePromise = (window as any).__membershipModulePromise;
  const moduleReady: Promise<unknown> =
    maybePromise && typeof maybePromise.then === "function"
      ? Promise.resolve(maybePromise).catch((err) => {
          call.moduleError = err;
          (window as any).__membershipModuleError = err;
          throw err;
        })
      : Promise.resolve(undefined);

  return moduleReady.then(
    () =>
      new Promise<void>((resolve, reject) => {
        const start = Date.now();
        call.moduleReadyAt = Date.now();
        const check = () => {
          // currentMembership may legitimately be an object with falsy fields; only treat null/undefined as "not ready".
          if ((window as any).currentMembership != null) {
            call.readyAt = Date.now();
            resolve();
            return;
          }
          if (Date.now() - start >= timeoutMs) {
            call.timedOutAt = Date.now();
            call.windowCurrentMembership = (window as any).currentMembership;
            reject(new Error("waitForMembership timeout: window.currentMembership is still null"));
            return;
          }
          setTimeout(check, 50);
        };
        check();
      }),
  );
}

const mobileReturnButton = document.querySelector("[data-return]");

if (!(window as any).__menuToggleBound) {
  (window as any).__menuToggleBound = true;
  document.addEventListener("click", (e) => {
    const target = e.target as HTMLElement | null;
    const toggler = target?.closest?.("[data-menu-toggle], [data-sidemenu-close]") as HTMLElement | null;
    if (!toggler) return;

    e.preventDefault();
    if (document.body.classList.contains("--menu-open")) {
      document.querySelectorAll(".sidemenu-content .--open").forEach((element) => {
        element.classList.remove("--open");
      });

      const firstLevel = document.querySelector<HTMLElement>(".--first-level");
      if (firstLevel) firstLevel.classList.remove("--first-level");
      const secondLevel = document.querySelector<HTMLElement>(".--second-level");
      if (secondLevel) secondLevel.classList.remove("--second-level");

      document.querySelectorAll(".sidenav__main a.active").forEach((link) => {
        link.classList.remove("active");
      });
      if (mobileReturnButton) {
        mobileReturnButton.classList.remove("visible");
      }
    }

    document.body.classList.toggle("--menu-open");
  });
}

let sidemenuContainer = document.querySelector(".sidemenu");
if (sidemenuContainer && !(window as any).__sidemenuBackdropBound) {
  (window as any).__sidemenuBackdropBound = true;
  sidemenuContainer.addEventListener("click", (e) => {
    const target = e.target;
    if (target !== e.currentTarget) return;

    e.preventDefault();
    if (document.body.classList.contains("--menu-open")) {
      document.querySelectorAll(".sidemenu-content .--open").forEach((element) => {
        element.classList.remove("--open");
      });

      const firstLevel = document.querySelector<HTMLElement>(".--first-level");
      if (firstLevel) firstLevel.classList.remove("--first-level");
      const secondLevel = document.querySelector<HTMLElement>(".--second-level");
      if (secondLevel) secondLevel.classList.remove("--second-level");

      document.querySelectorAll(".sidenav__main a.active").forEach((link) => {
        link.classList.remove("active");
      });

      if (mobileReturnButton) {
        mobileReturnButton.classList.remove("visible");
      }
    }

    document.body.classList.toggle("--menu-open");
  });
}

if (mobileReturnButton) {
  mobileReturnButton.addEventListener("click", (e) => {
    const secondLevel = document.querySelector<HTMLElement>(".--second-level");
    const firstLevel = document.querySelector<HTMLElement>(".--first-level");
    if (secondLevel) {
      secondLevel.classList.remove("--second-level");
      document.querySelector<HTMLElement>(".--open[data-subparentlink]")?.classList.remove("--open");
      document.querySelector<HTMLElement>(".--open[data-parentlink] .active")?.classList.remove("active");
    } else if (firstLevel) {
      firstLevel.classList.remove("--first-level");
      document.querySelector<HTMLElement>(".--open[data-parentlink]")?.classList.remove("--open");
      document.querySelector<HTMLElement>(".sidenav__main a.active")?.classList.remove("active");
      mobileReturnButton.classList.remove("visible");
    }
  });
}

let firstLevelNavItem = document.querySelectorAll('.sidenav__main > ul > li > a[href="#"]');
firstLevelNavItem.forEach((navItem) => {
  navItem.addEventListener("click", (e) => {
    if (mobileReturnButton) {
      mobileReturnButton.classList.add("visible");
    }

    const secondLevel = document.querySelector<HTMLElement>(".--second-level");
    if (secondLevel) {
      secondLevel.classList.remove("--second-level");
      document.querySelector<HTMLElement>(".--first-level")?.classList.add("--first-level");
      document.querySelector<HTMLElement>(".--open[data-parentlink]")?.classList.remove("--open");
      mobileReturnButton?.classList.remove("visible");
    }
  });
});

let sideNavParent = document.querySelectorAll('.sidemenu-content a[href="#"],.sidemenu-content a[href="/fr#"]');
let sideNavContainer = document.querySelector(".sidemenu-content");
sideNavParent.forEach((parent) => {
  parent.addEventListener("click", (e) => {
    e.preventDefault();
    const nextElementSibling = parent.nextElementSibling;
    if (!(nextElementSibling instanceof HTMLElement)) return;

    const parentLi = parent.parentElement;
    const parentUl = parentLi?.parentElement;
    if (!(parentUl instanceof HTMLElement)) return;

    let currentActiveSiblings = parentUl.querySelectorAll<HTMLAnchorElement>("a.active");
    currentActiveSiblings.forEach((activeSiblings) => {
      activeSiblings.classList.remove("active");
      const sibling = activeSiblings.nextElementSibling;
      if (sibling instanceof HTMLElement) sibling.classList.remove("--open");
    });

    if (nextElementSibling.hasAttribute("data-parentlink")) {
      sideNavParent.forEach((parent) => {
        parent.classList.remove("active");
        const sibling = parent.nextElementSibling;
        if (sibling instanceof HTMLElement && sibling.classList.contains("--open")) {
          sibling.classList.remove("--open");
        }
      });

      nextElementSibling.classList.add("--open");
      if (sideNavContainer instanceof HTMLElement) sideNavContainer.classList.add("--first-level");
      parent.classList.add("active");
    } else if (nextElementSibling.hasAttribute("data-subparentlink")) {
      nextElementSibling.classList.add("--open");
      if (sideNavContainer instanceof HTMLElement) sideNavContainer.classList.add("--second-level");
      parent.classList.add("active");
    } else {
      nextElementSibling.classList.remove("--open");
      if (sideNavContainer instanceof HTMLElement) sideNavContainer.classList.remove("--first-level");
    }
  });
});

let customMenu = document.querySelector(".custom-mobile-menu");
if (customMenu) {
  let cMChoices = new Choices(customMenu, {
    searchEnabled: false,
  });
  customMenu.addEventListener("change", (e) => {
    e.preventDefault();
    if (!(customMenu instanceof HTMLSelectElement)) return;
    const actionEl = document.querySelector(`[data-action=${customMenu.value}]`);
    if (actionEl instanceof HTMLElement) actionEl.click();
  });
}

function getCookie(name: string): string | undefined {
  let value = `; ${document.cookie}`;
  let parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(";").shift();
}

if (document.querySelector(".mediaclip-projects-section")) {
  import("../modules/mediaclip").then(({ initMediaClip }) => initMediaClip());
}

const productTabs = document.querySelector(".product-tabs");
if (productTabs) {
  const buttons = productTabs.querySelectorAll(".tab-button");
  buttons.forEach((button) => {
    button.addEventListener("click", (e) => {
      e.preventDefault();
      const activeButton = productTabs.querySelector<HTMLElement>("button.active");
      activeButton?.classList.remove("active");
      button.classList.add("active");

      const activeSection = productTabs.querySelector<HTMLElement>(".tabs-content .active");
      activeSection?.classList.remove("active");
      const sectionClass = (button as HTMLElement).dataset.tabSection;
      if (!sectionClass) return;
      const section = productTabs.querySelector<HTMLElement>(`.tabs-content .${sectionClass}`);
      section?.classList.add("active");
    });
  });
}

let printButtons = document.querySelectorAll("#print");
if (printButtons) {
  printButtons.forEach((button) => {
    button.addEventListener("click", (e) => {
      e.preventDefault();
      window.print();
    });
  });
}

function waitForSwiper(): Promise<void> {
  return new Promise<void>((resolve) => {
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds maximum

    const checkSwiper = () => {
      attempts++;

      if (typeof Swiper !== "undefined") {
        resolve();
        return;
      }

      if (attempts >= maxAttempts) {
        resolve();
        return;
      }

      setTimeout(checkSwiper, 100);
    };

    // Find the Swiper script
    const swiperScript = document.querySelector('script[src*="swiper-bundle.min.js"]');

    if (swiperScript) {
      swiperScript.addEventListener("load", () => {
        checkSwiper();
      });
    }

    // Start polling regardless
    checkSwiper();
  });
}

// Klaviyo Back in Stock init lives solely in src/modules/klaviyo.ts (dynamically
// imported above, gated on the onsite.js script tag being present). A full
// duplicate of this logic used to live inline here too — it ran unconditionally
// on every page and raced the module's init with a stale `replace_anchor: true`
// config (the module was fixed to `false` in e622d2873, but this copy never was),
// so whichever init won the race could leave the Notify Me widget misconfigured
// or missing. Removed rather than fixed in place: the module is the single
// source of truth now.

const globalLoaded = true;

function waitForLoyalty(): Promise<void> {
  return new Promise<void>((resolve) => {
    // First check if Loyalty programm is already available
    if (typeof _ALConfig !== "undefined") {
      resolve();
      return;
    }

    // Find the Loyalty programm script
    const loyaltyScript = document.querySelector<HTMLScriptElement>('script[src*="https://loyalty-admin.appstle.com/app/loyalty-widget.bundle.js?v=1748610372453"]');
    if (loyaltyScript) {
      // If script is still loading, wait for it
      if (!(loyaltyScript as HTMLScriptElement & { loaded?: boolean }).loaded) {
        loyaltyScript.addEventListener("load", () => {
          // Give a small delay for Swiper to initialize
          setTimeout(resolve, 100);
        });
      }
    } else {
      // If script not found, poll for Swiper object
      const checkLoyalty = () => {
        if (typeof _ALConfig !== "undefined") {
          resolve();
        } else {
          setTimeout(checkLoyalty, 100);
        }
      };
      checkLoyalty();
    }
  });
}

window.waitForLoyalty = waitForLoyalty;
(globalThis as any).waitForLoyalty = waitForLoyalty;

const loyaltyText = {
  fr: {
    level: "Niveau",
    remaining_start: "Tu es à",
    remaining_end: "du prochain niveau",
    points: "points",
    perks: "perks list",
    paid: "payé",
    end_by: "se terminant par",
    standard: "Poste Canada",
    express: "Poste Canada",
    items: "articles",
    item: "article",
    shipped_at: "Expédié le",
    in_production: "En production",
    gift_card: "Carte cadeau",
    remise: "de remise",
    free_standard_shipping: "la livraison standard est gratuite.",
    trendsetter_shipping: "profite de la livraison standard gratuite ou livraison prioritaire à 50% de rabais.",
    icon_shipping: "la livraison prioritaire est gratuite.",
    referal_title: "Référencement de 10$",
    expire_on: "Expire le",
    achat: "Achat",
    follow_tiktok: "Suivre KaseMe sur TikTok",
    follow_instagram: "Suivre KaseMe sur Instagram",
    follow_facebook: "Suivre KaseMe sur Facebook",
    birthday_gift_point: "Cadeau d'anniversaire",
    free_gift_title: "Obtiens ton cadeau iconique gratuit",
    dont_forget_apply_code: "N'oubliez pas d'appliquer le code promotionnel à la caisse",
  },
  en: {
    level: "Level",
    remaining_start: "You are at",
    remaining_end: "of the next level",
    points: "points",
    perks: "perks list",
    paid: "paid",
    end_by: "ending by",
    standard: "Canada Post",
    express: "Canada Post",
    order_state: "Order Status",
    items: "items",
    item: "item",
    shipped_at: "Shipped on",
    in_production: "In production",
    gift_card: "Gift card",
    remise: "discount",
    free_standard_shipping: "enjoy free standard delivery.",
    trendsetter_shipping: "enjoy free standard delivery or priority delivery at 50% off.",
    icon_shipping: "enjoy free priority delivery.",
    referal_title: "Referral",
    expire_on: "Expire on",
    achat: "Purchase",
    follow_tiktok: "Follow KaseMe on TikTok",
    follow_instagram: "Follow KaseMe on Instagram",
    follow_facebook: "Follow KaseMe on Facebook",
    birthday_gift_point: "Birthday gift point",
    free_gift_title: "Get your free iconic gift",
    dont_forget_apply_code: "Don't forget to apply the code at checkout",
  },
};

window.loyaltyText = loyaltyText;

function checkViewCartParameter(): void {
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('viewcart') !== 'true') return;
  const cartDrawer = document.querySelector<any>('cart-drawer');
  if (cartDrawer) setTimeout(() => cartDrawer.open(), 100);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', checkViewCartParameter);
} else {
  checkViewCartParameter();
}

const uniq = <T>(a: T[]): T[] => [...new Set(a)];

const currentLang = (document.documentElement.lang?.toLowerCase() === "fr" ? "fr" : "en") as SupportedLang;
window.currentLang = currentLang;
if (!customElements.get("product-form") && document.querySelector("product-form, quick-add-modal")) {
  import("../modules/product-form")
    .then(({ defineProductFormCustomElement }) => defineProductFormCustomElement())
    .catch((err) => {
      (window as any).__productFormImportError = err;
      console.log("product form import failed", err);
    });
}

// Simple cart count updater only
if (document.querySelector("#cart-icon-bubble")) {
  import("../modules/cart-count-updater").then(({ initCartCountUpdater }) => initCartCountUpdater());
}

if (document.querySelector('cart-drawer') && !(window as any).__kitGroupInitialized) {
  import("../modules/cart-kit-group").then(({ initCartKitGroupObserver, initKitRemoveListener }) => {
    if ((window as any).__kitGroupInitialized) return;
    (window as any).__kitGroupInitialized = true;
    initCartKitGroupObserver();
    const cartDrawer = document.querySelector('cart-drawer')!;
    initKitRemoveListener(cartDrawer);
  });
}

if (!(window as any).__giftWithPurchaseInitialized) {
  (window as any).__giftWithPurchaseInitialized = true;
  import("../modules/gift-with-purchase").then(({ initGiftWithPurchase }) => {
    initGiftWithPurchase();
  });
}

if (
  (document.getElementById('CartDrawer-CartItems') || document.getElementById('main-cart-items')) &&
  !(window as any).__giftGroupInitialized
) {
  (window as any).__giftGroupInitialized = true;
  import("../modules/gift-group").then(({ initGiftGroupObserver }) => initGiftGroupObserver());
}

if (document.querySelector('cart-drawer') && !(window as any).__bundleGroupInitialized) {
  import("../modules/cart-bundle-group").then(({ initCartBundleGroupObserver, initBundleRemoveListener }) => {
    if ((window as any).__bundleGroupInitialized) return;
    (window as any).__bundleGroupInitialized = true;
    initCartBundleGroupObserver();
    const cartDrawer = document.querySelector('cart-drawer')!;
    initBundleRemoveListener(cartDrawer);
  });
}

if (document.querySelector('button[disabled], button[aria-disabled="true"], [role="button"][aria-disabled="true"]')) {
  import("../modules/disabled-button-shake").then(({ initDisabledButtonShake }) => initDisabledButtonShake());
}

class CartPerformance {
  static #metric_prefix = "cart-performance";

  static createStartingMarker(benchmarkName: string) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName: string, event: Event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp,
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(metricName, `${metricName}:start`, `${metricName}:end`);
  }

  static measureFromMarker(benchmarkName: string, startMarker: PerformanceMark) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(metricName, startMarker.name, `${metricName}:end`);
  }

  static measure(benchmarkName: string, callback: () => void) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(metricName, `${metricName}:start`, `${metricName}:end`);
  }
}

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", (event) => {
      event.preventDefault();
      const cartItems = this.closest("cart-items") || this.closest("cart-drawer-items");
      const updateQuantity = (cartItems as any)?.updateQuantity;
      if (typeof updateQuantity !== "function") return;
      updateQuantity.call(cartItems, this.dataset.index || 0, 0, event);
    });
  }
}
if (!customElements.get("cart-remove-button")) {
  customElements.define("cart-remove-button", CartRemoveButton);
}

class CartItems extends HTMLElement {
  lineItemStatusElement: HTMLElement | null;
  cartUpdateUnsubscriber?: () => void;
  isEnterPressed = false;

  constructor() {
    super();
    this.lineItemStatusElement = document.getElementById("shopping-cart-line-item-status") || document.getElementById("CartDrawer-LineItemStatus");

    const debouncedOnChange = debounce((event: Event) => {
      this.onChange(event);
    }, 300);

    this.addEventListener("change", debouncedOnChange.bind(this));
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event: any) => {
      if (event?.source === "cart-items") {
        return;
      }
      // CART-DRAWER-ITEMS must NOT refetch the drawer here. Publishers are
      // required to call cartEl.renderContents() with fresh section data
      // themselves before publishing (product-form, gift-with-purchase,
      // kit-group, bundle-group, popular-atc, bundle-cart-add,
      // kit-atc-override and quick-order-list all do). This element's own
      // independent refetch-and-replaceWith would be redundant work, and its
      // unpredictable completion time relative to client-side DOM
      // enhancements applied after renderContents (kit/bundle grouping,
      // gift pairing, upsell rendering) races them out and wipes them.
      //
      // If you add a new cartUpdate publisher, render the drawer yourself.
      if (this.tagName === "CART-DRAWER-ITEMS") {
        return;
      }
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id: string | number) {
    const input = this.querySelector<HTMLInputElement>(`#Quantity-${id}`);
    if (!input) return;
    input.value = input.getAttribute("value") ?? "";
    this.isEnterPressed = false;
  }

  setValidity(event: Event, index: string, message: string) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    target.setCustomValidity(message);
    target.reportValidity();
    this.resetQuantityInput(index);
    target.select();
  }

  validateQuantity(event: Event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    const inputValue = parseInt(target.value);
    const index = target.dataset.index;
    if (!index) return;
    let message = "";

    const min = parseInt(target.dataset.min ?? "0");
    const max = parseInt(target.max);
    const step = parseInt(target.step);

    if (inputValue < min) {
      message = window.quickOrderListStrings?.min_error?.replace("[min]", String(min)) ?? "";
    } else if (inputValue > max) {
      message = window.quickOrderListStrings?.max_error?.replace("[max]", String(max)) ?? "";
    } else if (step && inputValue % step !== 0) {
      message = window.quickOrderListStrings?.step_error?.replace("[step]", String(step)) ?? "";
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      target.setCustomValidity("");
      target.reportValidity();
      this.updateQuantity(index, inputValue, event, document.activeElement instanceof HTMLElement ? document.activeElement.getAttribute("name") : null, target.dataset.quantityVariantId);
    }
  }

  onChange(event: Event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === "CART-DRAWER-ITEMS") {
      // Build theme-aware URL for cart drawer updates
      let fetchUrl = `${routes.cart_url}?section_id=cart-drawer-optimized-v2`;

      // Add theme preview parameter if we're in preview mode
      const currentParams = new URLSearchParams(window.location.search);
      for (const [key, value] of currentParams) {
        if (key === "preview_theme_id" || key.startsWith("_")) {
          fetchUrl += `&${key}=${value}`;
        }
      }

      return fetch(fetchUrl)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, "text/html");
          const selectors = ["cart-drawer-items", ".cart-drawer__footer"];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, "text/html");
          const sourceQty = html.querySelector("cart-items");
          if (sourceQty) this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRenderCartItems(): Array<{ id: string; section?: string; selector?: string }> {
    /*const mainCartItems = document.getElementById('main-cart-items');
    const mainCartFooter = document.getElementById('main-cart-footer');
    if (!mainCartItems || !mainCartFooter) return [];*/

    const sections = [
      {
        id: "main-cart-items",
        section: document.getElementById("main-cart-items")?.dataset.id,
        selector: ".js-contents",
      },
      {
        id: "cart-live-region-text",
        section: "cart-live-region-text",
        selector: ".shopify-section",
      },
      {
        id: "main-cart-footer",
        section: document.getElementById("main-cart-footer")?.dataset.id,
        selector: ".js-contents",
      },
      {
        id: "CartDrawer",
        section: "cart-drawer-optimized-v2",
        selector: ".drawer__inner",
      },
    ];
    return sections;
  }

  // Read the line before the request: once /cart/change answers, the removed line is gone from the DOM and the cart JSON.
  // Gift-with-purchase lines are skipped, they are not a customer purchase decision.
  getRemovedItemSnapshot(line: string | number, newQuantity: number) {
    const lineItem = document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
    if (!lineItem || lineItem.dataset.giftRule) return null;
    const input = lineItem.querySelector<HTMLInputElement>(".quantity__input");
    const previousQuantity = Number(input?.getAttribute("value"));
    const removedQuantity = previousQuantity - newQuantity;
    const linePrice = Number(lineItem.dataset.linePrice);
    if (!previousQuantity || removedQuantity <= 0 || Number.isNaN(linePrice)) return null;
    return {
      variantId: lineItem.dataset.variantId ?? "",
      name: lineItem.dataset.itemName ?? "",
      quantity: removedQuantity,
      price: linePrice / previousQuantity / 100,
    };
  }

  updateQuantity(line: string | number, quantity: number, event: Event, name?: string | null, variantId?: string) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRenderCartItems().map((section) => section.section),
      sections_url: window.location.pathname,
    });
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? "clear" : "change";
    const removedItem = this.getRemovedItemSnapshot(line, quantity);

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        CartPerformance.measure(`${eventTarget}:paint-updated-sections"`, () => {
          const quantityElement = document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          const quantityInput = quantityElement instanceof HTMLInputElement ? quantityElement : null;
          const items = document.querySelectorAll(".cart-item");

          if (parsedState.errors) {
            if (quantityInput) quantityInput.value = quantityInput.getAttribute("value") ?? "";
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle("is-empty", parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector<HTMLElement>("cart-drawer");
          const cartDrawerInner = cartDrawerWrapper?.querySelector<HTMLElement>(".drawer__inner");
          const cartFooter = document.getElementById("main-cart-footer");

          if (cartFooter) cartFooter.classList.toggle("is-empty", parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle("is-empty", parsedState.item_count === 0);
          if (cartDrawerInner) cartDrawerInner.classList.toggle("is-empty", parsedState.item_count === 0);
          this.getSectionsToRenderCartItems().forEach((section) => {
            const elementToReplace = (document.getElementById(section.id)?.querySelector(section.selector ?? "") as HTMLElement | null) || (document.getElementById(section.id) as HTMLElement | null);
            if (!elementToReplace) return;
            elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section as string], section.selector as string);
          });
          this.updateCartIconBubble(parsedState);
          if (removedItem) {
            trackEvent("remove_from_cart", {
              currency: parsedState.currency ?? "",
              value: Math.round(removedItem.price * removedItem.quantity * 100) / 100,
              items: [{ item_id: removedItem.variantId, item_name: removedItem.name, price: removedItem.price, quantity: removedItem.quantity }],
            });
            // Intelligems has no gtag bridge: its custom events come from window.igEvents (queue read once its script loads)
            const igEvents = ((window as any).igEvents = (window as any).igEvents || []);
            igEvents.push({
              event: "remove_from_cart",
              properties: { variant_id: removedItem.variantId, item_name: removedItem.name, quantity: removedItem.quantity, value: Math.round(removedItem.price * removedItem.quantity * 100) / 100 },
            });
          }
          const lineIndex = Number(line) - 1;
          const updatedValue = parsedState.items?.[lineIndex] ? parsedState.items[lineIndex].quantity : undefined;
          let message = "";
          if (quantityInput && items.length === parsedState.items.length && updatedValue !== parseInt(quantityInput.value)) {
            if (typeof updatedValue === "undefined") {
              message = window.cartStrings?.error ?? "";
            } else {
              message = window.cartStrings?.quantityError?.replace("[quantity]", String(updatedValue)) ?? "";
            }
          }
          this.updateLiveRegions(line, message);

          const lineItem = document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
          const focusTarget = name ? lineItem?.querySelector<HTMLElement>(`[name="${name}"]`) : null;
          if (lineItem && focusTarget) {
            cartDrawerWrapper ? trapFocus(cartDrawerWrapper, focusTarget) : focusTarget.focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector<HTMLElement>(".drawer__inner-empty") || cartDrawerWrapper, cartDrawerWrapper.querySelector<HTMLElement>("a"));
          } else if (document.querySelector(".cart-item") && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector<HTMLElement>(".cart-item__name"));
          }
        });
        CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);
        publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-items", cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll(".loading__spinner").forEach((overlay) => overlay.classList.add("hidden"));
        const errors = document.getElementById("cart-errors") || document.getElementById("CartDrawer-CartErrors");
        if (errors) errors.textContent = window.cartStrings?.error ?? "";
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateCartIconBubble(parsedState: any) {
    const cartIcon = document.querySelector("#cart-icon-bubble");
    if (!cartIcon) {
      return;
    }
    const itemCount = parsedState.item_count || 0;

    // Look for existing cart count bubble
    let cartCountBubble = cartIcon.querySelector<HTMLElement>(".cart-count-bubble");

    if (itemCount > 0) {
      // Create cart count bubble if it doesn't exist
      if (!cartCountBubble) {
        cartCountBubble = document.createElement("div");
        cartCountBubble.className = "cart-count-bubble";
        cartCountBubble.innerHTML = '<span class="visually-hidden">Items in cart: </span><span></span>';
        cartIcon.appendChild(cartCountBubble);
      }

      // Update the count
      const countSpan = cartCountBubble.querySelector("span:last-child");
      if (countSpan) {
        countSpan.textContent = String(itemCount);
      }

      // Show the bubble
      cartCountBubble.style.display = "";
    } else {
      // Hide the bubble if cart is empty
      if (cartCountBubble) {
        cartCountBubble.style.display = "none";
      }
    }
  }

  updateLiveRegions(line: string | number, message: string) {
    const lineItemError = document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    const lineItemErrorText = lineItemError?.querySelector<HTMLElement>(".cart-item__error-text");
    if (lineItemErrorText) lineItemErrorText.textContent = message;

    this.lineItemStatusElement?.setAttribute("aria-hidden", "true");

    const cartStatus = document.getElementById("cart-live-region-text") || document.getElementById("CartDrawer-LiveRegionText");
    cartStatus?.setAttribute("aria-hidden", "false");

    //cart upsell
    let cartDrawerUpsellSelect = document.querySelectorAll("#CartDrawer cart-drawer-upsell .upsell-product--default select");
    if (cartDrawerUpsellSelect.length) {
      const favPhone = getCookie("favPhone");
      cartDrawerUpsellSelect.forEach((selectEl) => {
        if (!(selectEl instanceof HTMLSelectElement)) return;
        selectEl.addEventListener("change", (e: Event) => {
          e.preventDefault();

          const target = e.target;
          if (!(target instanceof HTMLSelectElement)) return;
          const variantId = target.value;
          const form = target.parentElement?.parentElement?.querySelector("form");
          const idInput = form?.querySelector<HTMLInputElement>('input[name="id"]');
          if (!idInput) return;
          idInput.value = variantId;
        });

        if (favPhone) {
          if (selectEl.parentElement?.parentElement?.dataset.type == "Phone Case") {
            for (let i = 0; i < selectEl.options.length; i++) {
              const option = selectEl.options[i];
              const phoneModelAttr = option.getAttribute("data-phone-model");
              if (phoneModelAttr != "" && !phoneModelAttr?.includes("|")) {
                if (phoneModelAttr == favPhone) {
                  option.selected = true;
                  selectEl.value = option.value;
                  selectEl.dispatchEvent(new Event("change"));
                  return;
                }
              } else if (phoneModelAttr != "") {
                const phoneModels = phoneModelAttr?.split("|") ?? [];
                if (phoneModels.includes(favPhone)) {
                  option.selected = true;
                  selectEl.value = option.value;
                  selectEl.dispatchEvent(new Event("change"));
                  return;
                }
              }
            }
          }
        }
      });
    }

    setTimeout(() => {
      cartStatus?.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  getSectionInnerHTML(html: string, selector: string): string {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector)?.innerHTML ?? "";
  }

  enableLoading(line: string | number) {
    const mainCartItems = document.getElementById("main-cart-items") || document.getElementById("CartDrawer-CartItems");
    mainCartItems?.classList.add("cart__items--disabled");

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove("hidden"));

    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    this.lineItemStatusElement?.setAttribute("aria-hidden", "false");
  }

  disableLoading(line: string | number) {
    const mainCartItems = document.getElementById("main-cart-items") || document.getElementById("CartDrawer-CartItems");
    mainCartItems?.classList.remove("cart__items--disabled");

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add("hidden"));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add("hidden"));
  }
}
if (!customElements.get("cart-items")) {
  customElements.define("cart-items", CartItems);
}

if (!customElements.get("cart-note")) {
  customElements.define(
    "cart-note",
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          "input",
          debounce((event: Event) => {
            const target = event.target;
            if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) return;
            const body = JSON.stringify({ note: target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } }).then(() => CartPerformance.measureFromEvent("note-update:user-action", event));
          }, 300),
        );
      }
    },
  );
}

class CartDrawer extends HTMLElement {
  activeElement?: HTMLElement | null;
  productId?: string | number;

  constructor() {
    super();

    this.addEventListener("keyup", (evt: KeyboardEvent) => evt.code === "Escape" && this.close());
    this.querySelector("#CartDrawer-Overlay")?.addEventListener("click", this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector<HTMLElement>("#cart-icon-bubble");
    if (!cartLink) return;

    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event: KeyboardEvent) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy?: HTMLElement | null) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role")) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty") ? this.querySelector<HTMLElement>(".cart-drawer__empty") : document.getElementById("CartDrawer");
        const focusElement = this.querySelector<HTMLElement>(".drawer__inner") || this.querySelector<HTMLElement>(".drawer__close");
        if (containerToTrapFocusOn instanceof HTMLElement) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true },
    );

    document.body.classList.add("--no-scroll");
    document.dispatchEvent(new CustomEvent("cart-drawer:open"));
  }

  close() {
    this.classList.remove("active");
    if (this.activeElement) removeTrapFocus(this.activeElement);
    document.body.classList.remove("--no-scroll");
  }

  setSummaryAccessibility(cartDrawerNote: Element) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    const next = cartDrawerNote.nextElementSibling;
    if (next instanceof HTMLElement && next.getAttribute("id")) {
      cartDrawerNote.setAttribute("aria-controls", next.id);
    }

    cartDrawerNote.addEventListener("click", (event: Event) => {
      const currentTarget = event.currentTarget;
      if (!(currentTarget instanceof HTMLElement)) return;
      currentTarget.setAttribute("aria-expanded", String(!currentTarget.closest("details")?.hasAttribute("open")));
    });

    cartDrawerNote.parentElement?.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState: any) {
    const inner = this.querySelector<HTMLElement>(".drawer__inner");
    if (inner?.classList.contains("is-empty")) inner.classList.remove("is-empty");
    this.productId = parsedState?.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section ?? section.id], section.selector);
    });
    //cart upsell
    let cartDrawerUpsellSelect = document.querySelectorAll("#CartDrawer cart-drawer-upsell .upsell-product--default select");
    if (cartDrawerUpsellSelect.length) {
      const favPhone = getCookie("favPhone");
      cartDrawerUpsellSelect.forEach((selectEl) => {
        if (!(selectEl instanceof HTMLSelectElement)) return;
        selectEl.addEventListener("change", (e: Event) => {
          e.preventDefault();

          const target = e.target;
          if (!(target instanceof HTMLSelectElement)) return;
          const variantId = target.value;
          const form = target.parentElement?.parentElement?.querySelector("form");
          const idInput = form?.querySelector<HTMLInputElement>('input[name="id"]');
          if (!idInput) return;
          idInput.value = variantId;
        });

        if (favPhone) {
          if (selectEl.parentElement?.parentElement?.dataset.type == "Phone Case") {
            for (let i = 0; i < selectEl.options.length; i++) {
              const option = selectEl.options[i];
              const phoneModelAttr = option.getAttribute("data-phone-model");
              if (phoneModelAttr != "" && !phoneModelAttr?.includes("|")) {
                if (phoneModelAttr == favPhone) {
                  option.selected = true;
                  selectEl.value = option.value;
                  selectEl.dispatchEvent(new Event("change"));
                  return;
                }
              } else if (phoneModelAttr != "") {
                const phoneModels = phoneModelAttr?.split("|") ?? [];
                if (phoneModels.includes(favPhone)) {
                  option.selected = true;
                  selectEl.value = option.value;
                  selectEl.dispatchEvent(new Event("change"));
                  return;
                }
              }
            }
          }
        }
      });
    }

    setTimeout(() => {
      this.querySelector("#CartDrawer-Overlay")?.addEventListener("click", this.close.bind(this));
      this.open(this.activeElement ?? undefined);
    });
  }

  getSectionInnerHTML(html: string, selector = ".shopify-section"): string {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector)?.innerHTML ?? "";
  }

  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer-optimized-v2",
        selector: ".drawer__inner",
      },
    ];
  }

  getSectionDOM(html: string, selector = ".shopify-section"): Element | null {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector);
  }

  setActiveElement(element: HTMLElement | null) {
    this.activeElement = element;
  }
}
if (!customElements.get("cart-drawer")) {
  customElements.define("cart-drawer", CartDrawer);
}

// Plain rotator, no Swiper: Swiper measured the drawer while it was off-screen and left the
// second slide blank. Initialised in connectedCallback so it survives renderContents() swapping
// .drawer__inner via innerHTML.
class CartAnnouncementBar extends HTMLElement {
  private index = 0;
  private timer?: number;

  connectedCallback() {
    const slides = this.querySelectorAll<HTMLElement>(".cart-announcement-bar__slide");
    if (slides.length < 2) return;

    this.querySelector("[data-prev]")?.addEventListener("click", () => this.go(this.index - 1));
    this.querySelector("[data-next]")?.addEventListener("click", () => this.go(this.index + 1));
    this.start();
  }

  disconnectedCallback() {
    window.clearInterval(this.timer);
  }

  private start() {
    window.clearInterval(this.timer);
    this.timer = window.setInterval(() => this.go(this.index + 1, false), 5000);
  }

  private go(next: number, restart = true) {
    const slides = this.querySelectorAll<HTMLElement>(".cart-announcement-bar__slide");
    this.index = (next + slides.length) % slides.length;
    slides.forEach((slide, i) => slide.classList.toggle("is-active", i === this.index));
    if (restart) this.start();
  }
}
if (!customElements.get("cart-announcement-bar")) {
  customElements.define("cart-announcement-bar", CartAnnouncementBar);
}

class CartDrawerItems extends CartItems {
  getSectionsToRender(): Array<{ id: string; section?: string; selector?: string }> {
    const cartDrawer = document.getElementById("CartDrawer");
    if (!cartDrawer) return [];

    const mainCartItems = document.getElementById("main-cart-items");
    const mainCartFooter = document.getElementById("main-cart-footer");
    if (!mainCartItems || !mainCartFooter) return [];

    const sections = [
      {
        id: "CartDrawer",
        section: "cart-drawer-optimized-v2",
        selector: ".drawer__inner",
      },
    ];
    return sections;
  }
}
if (!customElements.get("cart-drawer-items")) {
  customElements.define("cart-drawer-items", CartDrawerItems);
}
